import { Action_Changeability } from "@opstel/gen/knop/ui/v1/model/action_pb";
import { DateTime } from "luxon";
import { ActionType, MovementActionType, SingleTrackActionType } from "./actionType";
import { Length } from "@shared/common/utils/length";
import { ShuntingUnit } from "@opstel/query/models/shuntingUnit";
import { StablingInfra, StablingInfraLookup } from "@opstel/query/models/location";
import { TrackCode, TrackWithGeometry } from "@opstel/query/models/track";

export type Changeability = "Fixed" | "Pinned" | "Open";
export type Orientation = "FirstUnitOnTargetSide" | "FirstUnitOnSourceSide";

interface BaseAction {
  id: string;
  name: string;
  startTime: DateTime<true>;
  endTime: DateTime<true>;
  changeability: Changeability;
  orientation: Orientation;
  shuntingUnitId: string;
}
export interface SingleTrackAction extends BaseAction {
  type: SingleTrackActionType;
  stablingInfra: string;
  distanceToSource: Length;
}
export interface MovementAction extends BaseAction {
  type: MovementActionType;
  tracks: string[];
  isNetworkMovement: boolean;
  distanceToSourceStart: Length;
  distanceToSourceEnd: Length;
  stablingInfraStart: string;
  stablingInfraEnd: string;
  movementCode?: string;
}

// Exported to keep operability with old API,
// when actions are fully converted this can be internal only
export type Action = SingleTrackAction | MovementAction;

export type WithShuntingUnit = {
  shuntingUnit?: ShuntingUnit;
};
export type WithTrackAndGeometry = {
  stablingInfraWithGeometry: StablingInfra;
};
export type WithTracksAndGeometry = {
  tracksWithGeometry: TrackWithGeometry[];
};
type ActionWithTrackAndGeometry =
  | (SingleTrackAction & WithTrackAndGeometry)
  | (MovementAction & WithTracksAndGeometry);

export const Action = {
  isMoveOrTeleport: (action: Action): action is MovementAction =>
    ActionType.isMoveOrTeleport(action.type),
  isMoveAction: (action: Action): action is MovementAction => ActionType.isMove(action.type),
  isSingleTrackAction: (action: Action): action is SingleTrackAction =>
    !ActionType.isMoveOrTeleport(action.type),
  isSplitOrCombine: (action: Action): action is SingleTrackAction =>
    ActionType.isSplitOrCombine(action.type),
  isActiveAt: (action: Action, time: DateTime) => {
    if (action.startTime > time || action.endTime < time) {
      return false;
    }
    // End is later than current time = always show
    if (action.endTime > time) {
      return true;
    }

    // Otherwise show if AtEnd action or departing move action
    if (action.type === "AtEnd") {
      return true;
    }

    if (action.type === "Move") {
      return TrackCode.isForeignTrack(action.tracks.at(-1)!);
    }

    return false;
  },
  enrichWithShuntingUnit: (
    action: Action,
    shuntingUnitMap: Record<string, ShuntingUnit>,
  ): Action & WithShuntingUnit => {
    const shuntingUnit = shuntingUnitMap[action.shuntingUnitId];
    if (!shuntingUnit) {
      console.warn(`Unknown shunting unit for action: ${JSON.stringify(action)}`);
    }
    return { ...action, shuntingUnit };
  },
  /**
   * Enriches actions with infra objects, based on the infra code.
   * Curently enriches the following:
   * - For actions on a single track, the stablingInfraWithGeometry property will be added and
   *   contain the track/track group on which the action takes place
   * - For actions that change tracks, the tracksWithGeometry property will be added. For moves
   *   this is a list of all the tracks which the move action passes over.
   *   The begin and end position are currently left as is and not enriched, because at this moment
   *   that is not needed. Feel free to change in the future.
   *
   * @param action Action to enrich with stabling infra based on code
   * @param infraLookup a map of codes to stabling infra objects
   */
  enrichWithInfra: (
    action: Action,
    infraLookup: StablingInfraLookup,
  ): ActionWithTrackAndGeometry => {
    if (Action.isSingleTrackAction(action)) {
      const stablingInfra = infraLookup[action.stablingInfra];
      if (!stablingInfra && !action.stablingInfra.startsWith("FT_")) {
        console.warn(
          `Unknown stablingInfra (${action.stablingInfra}) for action: ${JSON.stringify(action)}`,
        );
      }
      return { ...action, stablingInfraWithGeometry: stablingInfra };
    }
    return {
      ...action,
      tracksWithGeometry: action.tracks
        .filter((trackCode) => !TrackCode.isForeignTrack(trackCode))
        .map((trackCode) => {
          const track = infraLookup[trackCode];
          if (track?.type !== "Track") {
            console.warn(`Unknown track (${trackCode}) for move action: ${JSON.stringify(action)}`);
            return;
          }
          return track;
        })
        .filter((track) => !!track),
    };
  },
};

export function toggleChangeability(changeability: Changeability): Action_Changeability {
  const changeabilityMap: Record<Changeability, () => Action_Changeability> = {
    Open: () => Action_Changeability.PINNED,
    Pinned: () => Action_Changeability.OPEN,
    Fixed: () => Action_Changeability.FIXED,
  };
  return changeabilityMap[changeability]();
}
